import type { AvailableFilters } from "@/stores/documents/repository";
import { parseTagFilters } from "./parseTagFilters";

/** Minimal shape the tag filter reads — every document row carries these. */
export interface TagFilterableDocument {
  project_id?: number | string | null;
  categories?: Array<{ id: number }> | null;
  design_disciplines?: Array<{ id: number }> | null;
  design_stages?: Array<{ id: number }> | null;
  /** `null`/absent means unverified — the column is the boolean. */
  verified_at?: string | null;
}

const idsOf = (tags: Array<{ id: number }> | null | undefined): number[] =>
  Array.isArray(tags) ? tags.map((tag) => tag.id) : [];

/** A dimension with no selection is inert; otherwise the doc must carry one of its values. */
const matchesDimension = (selected: number[] | undefined, docValues: number[]): boolean =>
  !selected || selected.length === 0 || docValues.some((id) => selected.includes(id));

/**
 * Client-side equivalent of the tag filtering the Timeline/List views get for
 * free from the server (`loadDocuments` / the timeline RPC both take the parsed
 * filter object). The Folders tab reads its rows from the per-folder cache,
 * which is never re-queried per filter — so the shared page-level Filters
 * control applies here instead.
 *
 * Same semantics as the SQL side: AND across dimensions, OR within one.
 */
export function filterDocumentsByTags<T extends TagFilterableDocument>(
  documents: T[],
  activeFilters: string[],
  availableFilters: AvailableFilters | null
): T[] {
  if (activeFilters.length === 0) return documents;

  const { projectIds, categoryIds, designDisciplineIds, designStageIds, verifiedOnly } =
    parseTagFilters(activeFilters, availableFilters);

  const dimensions = [projectIds, categoryIds, designDisciplineIds, designStageIds];
  const noDimensions = dimensions.every((dimension) => !dimension || dimension.length === 0);
  // The Verified toggle has to be part of this bail-out test, not just the
  // predicate below — otherwise "Verified only" on its own would short-circuit
  // here and silently return every document.
  if (noDimensions && !verifiedOnly) return documents;

  return documents.filter((doc) => {
    const projectId = doc.project_id != null ? Number(doc.project_id) : null;
    return (
      (!verifiedOnly || doc.verified_at != null) &&
      matchesDimension(
        projectIds,
        projectId != null && !Number.isNaN(projectId) ? [projectId] : []
      ) &&
      matchesDimension(categoryIds, idsOf(doc.categories)) &&
      matchesDimension(designDisciplineIds, idsOf(doc.design_disciplines)) &&
      matchesDimension(designStageIds, idsOf(doc.design_stages))
    );
  });
}
